/**
 * 시간 객체 "매주" formatter
 *
 * @since 2018-04-02
 * @author kdydesign.kim
 */

// utils
import {bitToFormat} from '~/utils/format'
import {
  compareIsAfter,
  compareIsBeetween,
  formatDate,
  getStepDate
} from '~/utils/date'
import {i18n} from '~/plugins/i18n'

/**
 * 시간 객체 "매주" formatter
 *
 * @since 2018-06-20
 * @author kdydesign.kim
 *
 * @param data
 * @returns {string}
 */

const expToTime = function (exp = []) {
  if (exp && exp.length !== 8) return exp
  const startTimeHour = exp.substr(0, 2)
  const startTimeMin = exp.substr(2, 2)
  const endTimeHour = exp.substr(4, 2)
  const endTimeMin = exp.substr(6, 2)
  return `${startTimeHour}:${startTimeMin} ~ ${endTimeHour}:${endTimeMin}`
}
export function weekFormatter (data) {
  let usedFlag = false
  let timeFlag = false
  const weekStr = []

  if (data === 'Any') return 'Any'

  const use = [Number(data.sun.use), Number(data.mon.use), Number(data.tue.use), Number(data.wed.use), Number(data.thu.use), Number(data.fri.use), Number(data.sat.use)]
  const exp = [data.sun.exp[0], data.mon.exp[0], data.tue.exp[0], data.wed.exp[0], data.thu.exp[0], data.fri.exp[0], data.sat.exp[0]]

  if (use.every(i => i === 1)) usedFlag = true
  if (exp.every(i => i === '00002359')) timeFlag = true
  if (usedFlag && timeFlag) return 'Any'

  const week = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat']
  const weekStrs = {
    sun: i18n.t('cmn.weekSun'),
    mon: i18n.t('cmn.weekMon'),
    tue: i18n.t('cmn.weekTue'),
    wed: i18n.t('cmn.weekWed'),
    thu: i18n.t('cmn.weekThur'),
    fri: i18n.t('cmn.weekFri'),
    sat: i18n.t('cmn.weekSat')
  }

  _.forEach(week, day => {
    const use = _.get(data, [day, 'use'], 0)
    const exps = _.get(data, [day, 'exp'], [])
    if (use === 1 && exps[0] !== '00002359') {
      const rangeWeek = []
      _.forEach(exps, (exp) => {
        rangeWeek.push(expToTime(exp))
      })
      weekStr.push(`${weekStrs[day]}(${_.join(rangeWeek, ',')})`)
    } else if (use === 1 && exps[0] === '00002359') {
      weekStr.push(`${weekStrs[day]}(*)`)
    }
  })

  return weekStr.join(',')
}

/**
 * 시간 객체 "매월" formatter
 *
 * @since 2018-04-02
 * @author kdydesign.kim
 *
 * @param data
 */
export function monthFormatter (data) {
  if (data) {
    if (data.use === 1) {
      return bitToFormat(data.week, 'W')
    } else if (data.use === 2) {
      return data.period
    } else if (data.use === 3) {
      return bitToFormat(data.days, 'D')
    }
  }
}

/**
 * 시간 객체 "매년" formatter
 *
 * @since 2018-04-02
 * @author kdydesign.kim
 *
 * @param data
 */
export function yearFormatter (data) {
  if (data && data.use === 1) {
    return bitToFormat(data.exp, 'M')
  }
}

/**
 * 시간 객체 "기간" formatter
 *
 * @since 2018-06-20
 * @author kdydesign.kim
 *
 * @param data
 * @returns string
 */
export function rangeFormatter (data) {
  if (data && data.use === 1) {
    const resultData = []

    _.forEach(data.exp, (exp) => {
      const
        startYear = exp.substring(0, 4),
        startMonth = exp.substring(4, 6),
        startDay = exp.substring(6, 8),
        startHour = exp.substring(8, 10),
        startMin = exp.substring(10, 12),
        endYear = exp.substring(12, 16),
        endMonth = exp.substring(16, 18),
        endDay = exp.substring(18, 20),
        endHour = exp.substring(20, 22),
        endMin = exp.substring(22, 24)

      const startDate = `${startYear}.${startMonth}.${startDay} ${startHour}:${startMin}`
      const endDate = `${endYear}.${endMonth}.${endDay} ${endHour}:${endMin}`

      resultData.push(`${startDate} ~ ${endDate}`)
    })

    return _.join(resultData, ', ')
  }
}

/**
 * 시간 객체 시간 만료 icon 스타일
 *
 * @param currentDate
 * @param targetDate
 * @returns {string}
 *
 * TAMS 수정됨.
 * - targetDate localFormat false
 * - remainDays
 * - dateFormat = 'YYYY-MM-DD'
 */
export function getTimeoutDateStyle (currentDate, targetDate) {
  if (typeof targetDate !== 'undefined' && targetDate !== null) {
    const
      currDate = formatDate(currentDate, 'YYYY-MM-DD HH:mm'),
      tarDate = formatDate(targetDate, 'YYYY-MM-DD HH:mm', true),
      nextWeekDate = getStepDate(currDate, 'YYYY-MM-DD HH:mm', 7, 'days'),
      nextMonthDate = getStepDate(currDate, 'YYYY-MM-DD HH:mm', 30, 'days')

    // 만료
    if (compareIsAfter(currDate, tarDate, 'YYYY-MM-DD HH:mm')) {
      return 'r'
    } else {
      // 7일 전
      if (compareIsBeetween(tarDate, currDate, nextWeekDate, 'YYYY-MM-DD HH:mm')) {
        return 'o'
      }

      // 30일 전
      if (compareIsBeetween(tarDate, currDate, nextMonthDate, 'YYYY-MM-DD HH:mm')) {
        return 'y'
      }
    }
  }

  return 'b'
}

/**
 * 시간 객체 시간 만료 여부
 *
 * @param currentDate
 * @param targetDate
 * @returns {boolean}
 */
export function getCheckTimeoutDate (currentDate, targetDate) {
  if (targetDate > 0) {
    const
      currDate = formatDate(currentDate, 'YYYY-MM-DD HH:mm'),
      tarDate = formatDate(targetDate, 'YYYY-MM-DD HH:mm', true)

    // 만료
    if (compareIsAfter(currDate, tarDate, 'YYYY-MM-DD HH:mm')) {
      return true
    }
  }

  return false
}
